"use client";

import { useCallback } from "react";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { easeOutQuad } from "../../utils/easings";
import { bannerStyles, bannerIconStyles } from "./banner.styles";

import type { BannerProps } from "./banner.types";

import { Slot } from "radix-ui";
import { AnimatePresence, motion } from "motion/react";
import { IconChevronRight, IconCross } from "../../icons";
import { IconButton } from "../icon-button";

export function Banner(props: BannerProps) {
  const {
    children,
    variant = "default",
    closeable = false,
    isOpen: isOpenProp,
    defaultOpen = true,
    onOpenChange,
    className,
    asChild = false,
    icon = <IconChevronRight size="s" />,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useControllableState({
    prop: isOpenProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const classes = bannerStyles({
    variant,
    closeable,
    className,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const Component = asChild ? motion.create(Slot.Root) : motion.create("a");

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <Component
          className={classes}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: easeOutQuad }}
          {...rest}
        >
          <span className="flex items-center justify-center gap-x-xs w-full">
            <span>{children}</span>
            {icon ? <span className={bannerIconStyles()}>{icon}</span> : null}
          </span>
          {closeable && (
            <IconButton size="xs" variant="transparent" onClick={handleClose} aria-label="Close">
              <IconCross size="s" />
            </IconButton>
          )}
        </Component>
      )}
    </AnimatePresence>
  );
}

Banner.displayName = "Banner";
